/**
 * YourOfferCard — the offer this link made, and what may still be done to it.
 *
 * The product's own offer card (OfferSummary, in its gray container), with the
 * actions the link carries: counter while engaged, withdraw while pending or
 * engaged. A note under the summary says what the status means on this
 * property, from one table keyed by offer status and property status.
 */

import { useState } from 'react'
import { Button } from '@/common/components/button'
import { Text } from '@/common/components/text'
import type { CounterOfferCreate } from '@/features/offers/api/offers'
import { CounterOfferDialog } from '@/features/offers/components/CounterOfferDialog'
import { OfferSummary } from '@/features/offers/components/OfferSummary'
import { WithdrawOfferButton } from '@/features/offers/components/WithdrawOfferButton'
import {
  counterPartnerOffer,
  PARTNER_CAPABILITIES,
  withdrawPartnerOffer,
  type PartnerOffer,
  type PartnerProperty,
} from '../../api/partnerLinks'
import { mutationFailureOf, usePartnerMutation, type PartnerLinkSession } from '../../hooks/usePartnerMutation'
import * as styles from '../partnerCards.css'
import { offerNote } from './offerNotes'

export interface YourOfferCardProps {
  session: PartnerLinkSession
  offer: PartnerOffer
  property: PartnerProperty
  capabilities: readonly string[]
}

const TEMPORARY_COPY = {
  withdraw: 'Your offer was not withdrawn. Check your connection and try again.',
  counter: 'Your counter offer was not sent. Check your connection and try again.',
} as const

export function YourOfferCard({ session, offer, property, capabilities }: YourOfferCardProps) {
  const [counterOpen, setCounterOpen] = useState(false)
  const withdraw = usePartnerMutation(session, (offerId: string) => withdrawPartnerOffer(session.token, offerId))
  const counter = usePartnerMutation(session, ({ offerId, body }: { offerId: string; body: CounterOfferCreate }) =>
    counterPartnerOffer(session.token, offerId, body)
  )

  const canCounter = offer.status === 'engaged' && capabilities.includes(PARTNER_CAPABILITIES.offerNegotiate)
  const canWithdraw =
    (offer.status === 'pending' || offer.status === 'engaged') &&
    capabilities.includes(PARTNER_CAPABILITIES.offerWithdraw)
  const note = offerNote(offer, property.status)

  // The dialog closes once this resolves. Failures are shown on the card rather
  // than inside the dialog, which has no place for them, so it resolves either way.
  const submitCounter = async (offerId: string, body: CounterOfferCreate) => {
    try {
      await counter.mutateAsync({ offerId, body })
    } catch {
      // The mutation's state carries the failure; the card renders it below.
    }
  }

  const failure =
    withdraw.isError && mutationFailureOf(withdraw.error) === 'temporary'
      ? TEMPORARY_COPY.withdraw
      : counter.isError && mutationFailureOf(counter.error) === 'temporary'
        ? TEMPORARY_COPY.counter
        : null

  return (
    <section aria-label="Your offer">
      <OfferSummary
        offer={offer}
        negotiations={offer.negotiations}
        actions={
          <>
            <div className={styles.offerNote}>
              {note.title && (
                <Text variant="bodySm" weight="bold">
                  {note.title}
                </Text>
              )}
              <Text variant="bodySm" color="muted">
                {note.message}
              </Text>
            </div>
            {failure && (
              <span className={styles.errorMessage} role="alert">
                {failure}
              </span>
            )}
            {canCounter && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                width="full"
                disabled={counter.isPending}
                onClick={() => {
                  counter.reset()
                  setCounterOpen(true)
                }}
              >
                Submit counter offer
              </Button>
            )}
            {canWithdraw && (
              <WithdrawOfferButton
                offerPrice={offer.offer_price}
                isWithdrawing={withdraw.isPending}
                onConfirm={() => withdraw.mutate(offer.id)}
              />
            )}
          </>
        }
      />
      {canCounter && counterOpen && (
        <CounterOfferDialog
          open={counterOpen}
          onOpenChange={setCounterOpen}
          offer={{
            id: offer.id,
            offer_price: offer.offer_price,
            earnest_money_deposit: offer.earnest_money_deposit,
            closing_timeline: offer.closing_timeline,
            property: { address: { street_address: property.address?.street_address ?? null } },
          }}
          onSubmit={submitCounter}
        />
      )}
    </section>
  )
}
